import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Link2, FileText, Camera, Image as ImageIcon, Sparkles, Loader2, Check, Trash2, MapPin, ShoppingBag } from 'lucide-react';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import AddressAutocomplete from '../components/AddressAutocomplete';
import api from '../services/api';
import { useApp } from '../context/AppContext';
import { toast } from 'sonner';

/**
 * Compress large images entirely client-side so the user can throw ANY size
 * photo at us — we downscale to a max long-edge of 1600 px and re-encode as
 * JPEG at quality 0.8. That's more than enough for OCR + address extraction.
 */
async function compressToBase64(file, maxEdge = 1600, quality = 0.8) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new window.Image();
      img.onerror = reject;
      img.onload = () => {
        const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.src = String(reader.result);
    };
    reader.readAsDataURL(file);
  });
}

const ShopAnywhere = () => {
  const nav = useNavigate();
  const { format: formatBase, currency } = useApp();
  const format = (v) => formatBase(v, quote?.currency);
  const [mode, setMode] = useState('image');
  const [url, setUrl] = useState('');
  const [text, setText] = useState('');
  const [imageBase64, setImageBase64] = useState('');
  const [compressing, setCompressing] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [address, setAddress] = useState('');
  const [dropoffGeo, setDropoffGeo] = useState({ lat: null, lng: null });
  const [pickupGeo, setPickupGeo] = useState({ lat: null, lng: null });
  const [quote, setQuote] = useState(null); // { distance_km, delivery_fee, service_fee, estimated }
  const [quoting, setQuoting] = useState(false);
  const [note, setNote] = useState('');
  const [placing, setPlacing] = useState(false);
  // When we parse a photo we default to NOT importing invoice prices — a shop-
  // anywhere order is paid at the merchant, so the invoice price is only a
  // reference. The customer can flip this on to lock the invoice prices in.
  const [usePrices, setUsePrices] = useState(false);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  // Latest source values captured in refs so the auto-parse effect only fires
  // when the image itself changes (avoids re-parsing on every keystroke).
  const parseRef = useRef();

  const onPickImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setCompressing(true);
    try {
      const b64 = await compressToBase64(f);
      setImageBase64(b64);
      // Do NOT toast here — the auto-parse effect below will kick in and toast
      // once the AI is finished reading the invoice.
    } catch (err) {
      toast.error('Could not read that image — try another photo.');
    } finally { setCompressing(false); e.target.value = ''; }
  };

  const parse = async () => {
    if (mode === 'url' && !url) { toast.error('Paste the merchant URL'); return; }
    if (mode === 'text' && !text) { toast.error('Paste your cart text'); return; }
    if (mode === 'image' && !imageBase64) { toast.error('Take a photo or upload your invoice first'); return; }

    setParsing(true);
    try {
      const body = { url: mode === 'url' ? url : undefined,
                     text: mode === 'text' ? text : undefined,
                     image_base64: mode === 'image' ? imageBase64 : undefined };
      const { data } = await api.post('/onboarding/shop-anywhere/parse', body);
      // For photo-scanned invoices, DEFAULT to hiding prices — the driver pays
      // whatever the merchant charges at pickup, not whatever's on an old
      // invoice. Customer can flip the toggle on to lock invoice prices in.
      const shouldStripPrices = mode === 'image' && !usePrices;
      if (shouldStripPrices && Array.isArray(data.items)) {
        data.items = data.items.map((it) => ({ ...it, price: 0 }));
      }
      setParsed(data);
      // Prefill delivery address from the invoice if AI could read it.
      if (data?.customer?.address && !address) {
        setAddress(data.customer.address);
        toast.info('Delivery address auto-filled from the invoice — verify it below.');
      }
      if (!data.items || data.items.length === 0) {
        toast.warning('We couldn\'t detect items. Try a clearer input.');
      } else if (shouldStripPrices) {
        toast.success(`Found ${data.items.length} item${data.items.length === 1 ? '' : 's'}. Prices left blank — driver pays whatever the merchant charges.`);
      } else {
        toast.success(`Found ${data.items.length} item${data.items.length === 1 ? '' : 's'} — edit and confirm.`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'AI parse failed');
    } finally { setParsing(false); }
  };
  parseRef.current = parse;

  // Auto-parse the invoice as soon as a fresh photo is captured. We watch the
  // base64 payload directly — every new photo creates a new string so this
  // effect fires exactly once per capture.
  useEffect(() => {
    if (!imageBase64 || mode !== 'image') return;
    if (parsing) return;
    // Reset the previous parse output so the UI clearly re-renders.
    setParsed(null);
    const t = setTimeout(() => { parseRef.current?.(); }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [imageBase64]);

  const updateItem = (i, k, v) => {
    setParsed((p) => ({ ...p, items: p.items.map((it, idx) => idx === i ? { ...it, [k]: v } : it) }));
  };
  const removeItem = (i) => setParsed((p) => ({ ...p, items: p.items.filter((_, idx) => idx !== i) }));

  const subtotal = (parsed?.items || []).reduce((s, it) => s + (Number(it.price) || 0) * (Number(it.qty) || 1), 0);
  const delivery = quote?.delivery_fee ?? (parsed ? 5.99 : 0);
  const service = quote?.service_fee ?? (parsed ? Math.max(2, subtotal * 0.08) : 0);
  const total = subtotal + delivery + service;

  // Auto-refresh the delivery quote whenever both endpoints (pickup on the
  // invoice + customer delivery address) have real coords and the subtotal
  // changes. Debounced to avoid spamming the API on every keystroke.
  const refreshQuote = useCallback(async () => {
    if (pickupGeo.lat == null || dropoffGeo.lat == null) {
      setQuote(null);
      return;
    }
    setQuoting(true);
    try {
      const { data } = await api.post('/onboarding/shop-anywhere/quote', {
        pickup_lat: pickupGeo.lat, pickup_lng: pickupGeo.lng,
        dropoff_lat: dropoffGeo.lat, dropoff_lng: dropoffGeo.lng,
        subtotal,
      });
      setQuote(data);
    } catch { /* silent — fall back to legacy flat fee */ } finally { setQuoting(false); }
  }, [pickupGeo, dropoffGeo, subtotal]);

  useEffect(() => {
    const t = setTimeout(refreshQuote, 350);
    return () => clearTimeout(t);
  }, [refreshQuote]);

  const placeOrder = async () => {
    if (!address) { toast.error('Add your delivery address'); return; }
    if (!parsed?.vendor?.address) { toast.error('Add the merchant pickup address before placing the order'); return; }
    if (!parsed?.items?.length) return;
    setPlacing(true);
    try {
      const { data } = await api.post('/onboarding/shop-anywhere/order', {
        items: parsed.items.map((it) => ({ name: it.name, price: Number(it.price) || 0, qty: Number(it.qty) || 1 })),
        subtotal,
        currency: currency?.code || parsed?.currency || 'USD',
        vendor: parsed?.vendor || {},
        address, note,
        pickup_lat: pickupGeo.lat, pickup_lng: pickupGeo.lng,
        dropoff_lat: dropoffGeo.lat, dropoff_lng: dropoffGeo.lng,
      });
      toast.success('Order dispatched — a driver is on the way to pick it up.');
      nav(`/track/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not place order');
    } finally { setPlacing(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header showSearch={false} />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => nav(-1)} className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900 mb-4">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </button>

        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-full bg-amber-500 flex items-center justify-center shadow-lg shadow-amber-500/30">
            <ShoppingBag className="h-6 w-6 text-white" strokeWidth={2.3} />
          </div>
          <div>
            <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">Shop anywhere</h1>
            <p className="text-slate-500 mt-1">Snap a photo of any invoice — we read the pickup & delivery addresses and quote the fee.</p>
          </div>
        </div>

        <div className="mt-7 bg-white border border-slate-200 rounded-2xl p-5">
          <Tabs value={mode} onValueChange={setMode}>
            <TabsList className="grid grid-cols-3 bg-slate-100" data-testid="shop-anywhere-tabs">
              <TabsTrigger value="image" data-testid="tab-image"><Camera className="h-4 w-4 mr-1.5" /> Photo of invoice</TabsTrigger>
              <TabsTrigger value="url" data-testid="tab-url"><Link2 className="h-4 w-4 mr-1.5" /> URL</TabsTrigger>
              <TabsTrigger value="text" data-testid="tab-text"><FileText className="h-4 w-4 mr-1.5" /> Paste text</TabsTrigger>
            </TabsList>

            <TabsContent value="url" className="mt-4">
              <Label className="text-xs text-slate-600">Merchant URL</Label>
              <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://acme-bakery.com/cart" className="h-11 mt-1" data-testid="shop-url-input" />
            </TabsContent>
            <TabsContent value="text" className="mt-4">
              <Label className="text-xs text-slate-600">Paste your cart contents</Label>
              <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="2 × Margherita Pizza — $12.50&#10;1 × Tiramisu — $7.00&#10;Subtotal: $32.00" rows={6} className="mt-1" data-testid="shop-text-input" />
            </TabsContent>
            <TabsContent value="image" className="mt-4">
              {/* Camera-first flow: `capture=environment` opens the rear camera on mobile.
                  `Choose photo` opens the gallery/file picker on any device. Any size is
                  fine — we downscale + re-encode to JPEG client-side before upload. */}
              <input type="file" accept="image/*" capture="environment" ref={cameraRef} onChange={onPickImage} className="hidden" data-testid="shop-camera-input" />
              <input type="file" accept="image/*" ref={fileRef} onChange={onPickImage} className="hidden" data-testid="shop-image-input" />

              <button
                onClick={() => cameraRef.current?.click()}
                disabled={compressing}
                data-testid="shop-image-preview"
                className="w-full min-h-40 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 hover:border-amber-400 hover:bg-amber-50/40 flex flex-col items-center justify-center gap-2 text-slate-500 relative overflow-hidden"
              >
                {compressing ? (
                  <><Loader2 className="h-6 w-6 animate-spin text-amber-500" /><span className="text-sm">Preparing image…</span></>
                ) : imageBase64 ? (
                  <img src={imageBase64} alt="invoice" className="w-full max-h-72 object-contain bg-white" />
                ) : (
                  <>
                    <Camera className="h-7 w-7" />
                    <div className="text-sm font-medium text-slate-700">Tap to take a photo of your invoice</div>
                    <div className="text-[11px] text-slate-500">Any size works — we shrink it before sending.</div>
                  </>
                )}
              </button>

              <div className="mt-3 grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => cameraRef.current?.click()}
                  disabled={compressing}
                  data-testid="shop-take-photo-btn"
                  className="h-11"
                >
                  <Camera className="h-4 w-4 mr-1.5" /> Take photo
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileRef.current?.click()}
                  disabled={compressing}
                  data-testid="shop-upload-btn"
                  className="h-11"
                >
                  <ImageIcon className="h-4 w-4 mr-1.5" /> Choose from device
                </Button>
              </div>
              {imageBase64 && (
                <button
                  onClick={() => setImageBase64('')}
                  className="mt-2 text-xs text-slate-500 hover:text-rose-600 inline-flex items-center gap-1"
                  data-testid="shop-image-clear"
                >
                  <Trash2 className="h-3 w-3" /> Remove image
                </button>
              )}
              <div className="mt-3 flex items-center gap-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
                <input
                  id="use-invoice-prices"
                  type="checkbox"
                  checked={usePrices}
                  onChange={(e) => setUsePrices(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-amber-500 focus:ring-amber-500"
                  data-testid="use-invoice-prices-toggle"
                />
                <label htmlFor="use-invoice-prices" className="text-[12px] text-slate-600 cursor-pointer select-none">
                  Also import the prices from the invoice (usually not needed — the driver pays whatever the merchant charges).
                </label>
              </div>

              <p className="mt-3 text-[11px] text-slate-500 leading-relaxed">
                Point the camera at any receipt or invoice — screenshots, printed slips, hand-written totals.
                Our AI reads both the <span className="font-semibold text-slate-700">merchant pickup</span> address
                and (if visible) your <span className="font-semibold text-slate-700">delivery</span> address to compute the exact fee.
              </p>
            </TabsContent>
          </Tabs>

          {/* Only show the manual parse button for URL / text modes. In the
              photo flow the AI kicks in automatically once the photo is
              captured. */}
          {mode !== 'image' && (
            <div className="mt-4 flex justify-end">
              <Button onClick={parse} disabled={parsing || compressing} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="shop-parse-btn">
                {parsing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
                {parsing ? 'Reading your invoice…' : 'Read invoice with AI'}
              </Button>
            </div>
          )}
          {mode === 'image' && (parsing || compressing) && (
            <div className="mt-3 flex items-center gap-2 text-sm text-amber-700" data-testid="shop-auto-parsing">
              <Loader2 className="h-4 w-4 animate-spin" />
              {compressing ? 'Preparing your image…' : 'Reading your invoice with AI…'}
            </div>
          )}
        </div>

        {parsed && (
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-5 gap-6">
            <section className="lg:col-span-3 bg-white border border-slate-200 rounded-2xl p-5">
              <div className="flex items-center justify-between">
                <h2 className="font-semibold text-slate-900">Your cart</h2>
                {parsed.vendor?.name && <div className="text-xs text-slate-500">from <b className="text-slate-700">{parsed.vendor.name}</b></div>}
              </div>

              {/* Vendor pickup details — captured from the receipt or URL.
                  Customer must verify these so the driver picks up at the right address. */}
              <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50/60 p-3.5" data-testid="vendor-pickup-block">
                <div className="text-[11px] uppercase tracking-widest text-amber-800 font-semibold flex items-center gap-1.5">
                  <MapPin className="h-3 w-3" /> Pickup at merchant — verify
                </div>
                <div className="mt-2.5 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  <div>
                    <Label className="text-[11px] text-slate-600">Merchant name</Label>
                    <Input
                      value={parsed.vendor?.name || ''}
                      onChange={(e) => setParsed((p) => ({ ...p, vendor: { ...p.vendor, name: e.target.value } }))}
                      placeholder="Merchant name"
                      className="h-9 mt-1"
                      data-testid="vendor-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-[11px] text-slate-600">Phone (optional)</Label>
                    <Input
                      value={parsed.vendor?.phone || ''}
                      onChange={(e) => setParsed((p) => ({ ...p, vendor: { ...p.vendor, phone: e.target.value } }))}
                      placeholder="+1 555-…"
                      className="h-9 mt-1"
                      data-testid="vendor-phone-input"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-[11px] text-slate-600">Pickup address</Label>
                    <AddressAutocomplete
                      value={parsed.vendor?.address || ''}
                      onChange={(addr, geo) => {
                        const formatted = typeof addr === 'string' ? addr : addr?.formatted || '';
                        setParsed((p) => ({ ...p, vendor: { ...p.vendor, address: formatted } }));
                        setPickupGeo({ lat: geo?.lat ?? null, lng: geo?.lng ?? null });
                      }}
                      placeholder="Pickup address as shown on receipt"
                    />
                  </div>
                </div>
                {!parsed.vendor?.address && (
                  <p className="mt-2 text-[11px] text-amber-700">
                    AI couldn&apos;t read a pickup address from the input. Please type it so the driver knows where to go.
                  </p>
                )}
              </div>

              <div className="mt-4 space-y-2">
                {parsed.items?.length > 0 && !usePrices && (
                  <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 mb-2" data-testid="shop-no-prices-hint">
                    Prices are not listed — the driver will pay whatever the merchant charges when picking up.
                  </div>
                )}
                {(parsed.items || []).map((it, i) => (
                  <div key={i} className="flex items-center gap-2 py-2 border-b border-slate-100" data-testid={`shop-item-${i}`}>
                    <Input value={it.name} onChange={(e) => updateItem(i, 'name', e.target.value)} className="h-9 flex-1" />
                    <Input value={it.qty} onChange={(e) => updateItem(i, 'qty', e.target.value)} type="number" min={1} className="h-9 w-16 text-center" />
                    {usePrices && (
                      <>
                        <span className="text-slate-400 text-sm">×</span>
                        <Input value={it.price} onChange={(e) => updateItem(i, 'price', e.target.value)} type="number" step="0.01" className="h-9 w-24 text-right" />
                      </>
                    )}
                    <button onClick={() => removeItem(i)} className="p-2 text-slate-400 hover:text-rose-500"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
                {(!parsed.items || parsed.items.length === 0) && <div className="py-6 text-center text-sm text-slate-500">No items yet — go back and try again.</div>}
              </div>
            </section>

            <aside className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-5 h-fit">
              <h2 className="font-semibold text-slate-900">Deliver to</h2>
              <AddressAutocomplete
                value={address}
                onChange={(a, geo) => {
                  setAddress(typeof a === 'string' ? a : a?.formatted || '');
                  setDropoffGeo({ lat: geo?.lat ?? null, lng: geo?.lng ?? null });
                }}
                placeholder="Your delivery address"
              />
              <Label className="text-xs text-slate-600 mt-3 block">Notes for the driver</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Apt # / specific items the driver should confirm" rows={3} className="mt-1" />

              {/* Distance-based delivery fee indicator */}
              {(pickupGeo.lat != null && dropoffGeo.lat != null) ? (
                <div className="mt-4 rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2 text-[12px] text-emerald-800 flex items-center gap-2" data-testid="shop-quote-indicator">
                  <Sparkles className="h-3.5 w-3.5" />
                  {quoting
                    ? 'Calculating fee…'
                    : quote?.distance_km != null
                      ? <span><b>{quote.distance_km} km</b> · fee computed from the two addresses on the invoice.</span>
                      : 'Fee ready'}
                </div>
              ) : parsed && (
                <div className="mt-4 rounded-lg bg-amber-50 border border-amber-100 px-3 py-2 text-[12px] text-amber-800" data-testid="shop-quote-hint">
                  Pick both addresses from the suggestions to unlock the exact fee — otherwise we&apos;ll use a flat {format(5.99)}.
                </div>
              )}

              <div className="mt-4 text-sm space-y-1.5">
                {usePrices ? (
                  <Row label="Subtotal" value={format(subtotal)} />
                ) : (
                  <Row label="Item cost" value="Paid at merchant" />
                )}
                <Row label={`Pickup + delivery${quote?.distance_km != null ? ` (${quote.distance_km} km)` : ''}`} value={format(delivery)} />
                <Row label="Service fee" value={format(service)} />
                <div className="h-px bg-slate-200 my-2" />
                <Row label={usePrices ? 'Total' : 'ShipitNow fees'} value={format(total)} bold />
              </div>
              <Button onClick={placeOrder} disabled={placing || !parsed.items?.length}
                className="mt-4 w-full h-11 bg-amber-500 hover:bg-amber-600 text-white" data-testid="shop-place-order-btn">
                {placing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <MapPin className="h-4 w-4 mr-1.5" />}
                {placing ? 'Dispatching…' : `Place order • ${format(total)}`}
              </Button>
              <p className="mt-2 text-[11px] text-slate-400 text-center">Driver picks up + pays at the merchant on your behalf.</p>
            </aside>
          </div>
        )}
      </main>
    </div>
  );
};

const Row = ({ label, value, bold }) => (
  <div className="flex items-center justify-between">
    <span className={bold ? 'text-slate-900 font-semibold' : 'text-slate-600'}>{label}</span>
    <span className={bold ? 'text-slate-900 font-semibold' : 'text-slate-900'}>{value}</span>
  </div>
);

export default ShopAnywhere;
